import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { mapMeetingToRow } from "./meetingDetailsUtils";
import {
  STATUS_TYPES,
  DEFAULT_OPEN,
  MEETING_LIST_DEFAULT_SORT,
} from "./meetingDetailsConstants";
import { createMeetingListDatasource } from "./meetingDetailsInfiniteDatasource";

/**
 * View Meeting lists: AG Grid Infinite Row Model + server sort/search/column filters.
 */
export function useMeetingListData({
  lang,
  clearAllSelections,
  search = "",
  columnFiltersRef,
  gridApiRef,
}) {
  const [status, setStatus] = useState(() =>
    STATUS_TYPES.map((t) => ({
      type: t,
      meetings: [],
      totalCount: 0,
      page: 1,
      totalPages: 1,
    }))
  );
  const [, setOpenSections] = useState(() => new Set(DEFAULT_OPEN));
  const [sectionLoaders, setSectionLoaders] = useState({});
  const [loader, setLoader] = useState(true);
  const [allMeeting, setAllMeeting] = useState(false);
  const [meetingListSort, setMeetingListSort] = useState(() => ({
    ...MEETING_LIST_DEFAULT_SORT,
  }));

  const meetingListRequestRef = useRef({});
  const hiddenRowIdsRef = useRef(new Set());

  const mapMeetingData = useCallback(
    (rawArray) => (rawArray || []).map((d) => mapMeetingToRow(d, lang)),
    [lang]
  );

  const onMeetingBlockLoaded = useCallback((statusType, payload) => {
    setStatus((prev) =>
      prev.map((s) => {
        if (s.type !== statusType) return s;
        const byId = new Map(
          (s.meetings || []).map((m) => [String(m["Meeting Id"]), m])
        );
        (payload.rows || []).forEach((r) => {
          if (r && r["Meeting Id"] != null) {
            byId.set(String(r["Meeting Id"]), r);
          }
        });
        return {
          ...s,
          meetings: Array.from(byId.values()),
          totalCount: payload.totalCount ?? s.totalCount,
          totalPages: payload.totalPages ?? s.totalPages,
          page: Math.max(s.page, payload.page ?? 1),
        };
      })
    );
  }, []);

  useEffect(() => {
    meetingListRequestRef.current = {
      search: typeof search === "string" ? search.trim() : "",
      allMeeting: allMeeting === true,
      sortField: meetingListSort.sortField,
      sortDir: meetingListSort.sortDir,
      columnFiltersRef,
      mapMeetingData,
      hiddenRowIds: hiddenRowIdsRef,
    };
  }, [
    search,
    allMeeting,
    meetingListSort.sortField,
    meetingListSort.sortDir,
    columnFiltersRef,
    mapMeetingData,
  ]);

  const meetingDatasources = useMemo(() => {
    const map = {};
    STATUS_TYPES.forEach((st) => {
      map[st] = createMeetingListDatasource(st, meetingListRequestRef, {
        onLoaded: (payload) => onMeetingBlockLoaded(st, payload),
        setSectionLoading: (loading) => {
          setSectionLoaders((p) => ({ ...p, [st]: loading }));
        },
      });
    });
    return map;
  }, [onMeetingBlockLoaded]);

  const purgeSection = useCallback((statusType) => {
    setStatus((prev) =>
      prev.map((s) =>
        s.type === statusType ? { ...s, meetings: [] } : s
      )
    );
    gridApiRef?.current?.[statusType]?.purgeInfiniteCache?.();
  }, [gridApiRef]);

  /**
   * Refresh one section (after bulk update, manual refresh, column filter).
   */
  const fetchSectionData = useCallback(
    (statusType) => {
      purgeSection(statusType);
    },
    [purgeSection]
  );

  /**
   * Optimistically hide meetings from a section: hides rows from the grid datasource,
   * decrements the section count, and purges the cache so the grid re-renders without them.
   */
  const optimisticallyHideMeetings = useCallback(
    (meetingIds, fromStatusType) => {
      const ids = Array.isArray(meetingIds) ? meetingIds : [meetingIds];
      ids.forEach((id) => hiddenRowIdsRef.current.add(String(id)));
      setStatus((prev) =>
        prev.map((s) => {
          if (s.type !== fromStatusType) return s;
          return { ...s, totalCount: Math.max(0, s.totalCount - ids.length) };
        })
      );
      gridApiRef?.current?.[fromStatusType]?.purgeInfiniteCache?.();
    },
    [gridApiRef]
  );

  const clearHiddenMeetings = useCallback((meetingIds) => {
    const ids = Array.isArray(meetingIds) ? meetingIds : [meetingIds];
    ids.forEach((id) => hiddenRowIdsRef.current.delete(String(id)));
  }, []);

  const getallmeetings = useCallback(
    async (sectionKeys) => {
      setLoader(true);
      clearAllSelections();
      const keys =
        Array.isArray(sectionKeys) && sectionKeys.length > 0
          ? sectionKeys
          : STATUS_TYPES;
      setStatus((prev) =>
        prev.map((s) =>
          keys.includes(s.type) ? { ...s, meetings: [] } : s
        )
      );
      keys.forEach((st) => {
        gridApiRef?.current?.[st]?.purgeInfiniteCache?.();
      });
      setLoader(false);
    },
    [clearAllSelections, gridApiRef]
  );

  const getallmeetingsRef = useRef(getallmeetings);
  getallmeetingsRef.current = getallmeetings;
  const sortChangeSkipInitialRef = useRef(true);
  useEffect(() => {
    if (sortChangeSkipInitialRef.current) {
      sortChangeSkipInitialRef.current = false;
      return;
    }
    void getallmeetingsRef.current(STATUS_TYPES);
  }, [meetingListSort]);

  return {
    status,
    setStatus,
    sectionLoaders,
    setOpenSections,
    loader,
    setLoader,
    allMeeting,
    setAllMeeting,
    meetingListSort,
    setMeetingListSort,
    fetchSectionData,
    getallmeetings,
    meetingDatasources,
    optimisticallyHideMeetings,
    clearHiddenMeetings,
  };
}
